import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { useApi } from "../hooks/useApi";
import Card from "../components/Card";

const VendorControlMapping = ({ vendorId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedFrameworks, setExpandedFrameworks] = useState({});
    const apiCall = useApi();

    useEffect(() => {
        const fetchControlMapping = async () => {
            try {
                const result = await apiCall("GET", `/TPRMVendor/${vendorId}/control-mapping`);
                setData(result);
            } catch (err) {} finally {
                setLoading(false);
            }
        };
        fetchControlMapping();
    }, [vendorId]);

    const toggleFramework = (framework) => {
        setExpandedFrameworks(prev => ({ ...prev, [framework]: !prev[framework] }));
    };

    if (loading) {
        return (
            <Card>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Card>
        );
    }

    if (!data || data.frameworkBreakdown?.length === 0) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-2">Risk to Control Mapping</h3>
                <p className="text-gray-500 text-sm">No framework or control mappings found for this vendor's risks.</p>
            </Card>
        );
    }

    // Build a lookup of control attributions per framework for quick access
    const getAttribution = (fb, controlId) => {
        return fb.controlAttributions?.find(a => a.controlId === controlId);
    };

    const overallAddressed = data.totalControlsAddressed || 0;
    const overallTotal = data.totalUniqueControlsImpacted || 0;
    const readinessPct = overallTotal > 0 ? Math.round((overallAddressed / overallTotal) * 100) : 0;

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Risk to Control Mapping</h3>
                <div className="flex items-center gap-4">
                    {overallAddressed > 0 && (
                        <span className="text-sm font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                            {overallAddressed} of {overallTotal} controls addressed ({readinessPct}%)
                        </span>
                    )}
                    <span className="text-sm text-gray-500">
                        {overallTotal} controls across {data.frameworkBreakdown.length} frameworks
                    </span>
                </div>
            </div>

            <div className="space-y-2">
                {data.frameworkBreakdown.map((fb) => (
                    <div key={fb.framework} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                            onClick={() => toggleFramework(fb.framework)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                {expandedFrameworks[fb.framework]
                                    ? <ChevronDown className="h-4 w-4 text-gray-500" />
                                    : <ChevronRight className="h-4 w-4 text-gray-500" />
                                }
                                <span className="font-medium text-gray-800">{fb.framework}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                                {fb.controlsAddressed > 0 && (
                                    <span className="text-emerald-600 font-medium">
                                        {fb.controlsAddressed} of {fb.controlCount} addressed
                                    </span>
                                )}
                                <span className="text-gray-500">
                                    {fb.controlCount} control{fb.controlCount !== 1 ? "s" : ""}
                                </span>
                                <span className="text-gray-500">
                                    {fb.riskCount} risk{fb.riskCount !== 1 ? "s" : ""}
                                </span>
                            </div>
                        </button>
                        {expandedFrameworks[fb.framework] && fb.controls?.length > 0 && (
                            <div className="px-4 py-3 border-t border-gray-200">
                                <div className="flex flex-wrap gap-2">
                                    {fb.controls.map((control) => {
                                        const attr = getAttribution(fb, control);
                                        const isAddressed = attr?.isAddressed;
                                        return (
                                            <span
                                                key={control}
                                                className={`px-2 py-1 text-xs rounded-md border inline-flex items-center gap-1 ${
                                                    isAddressed
                                                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                                        : "bg-blue-50 text-blue-800 border-blue-200"
                                                }`}
                                                title={attr ? `${attr.remediatedRisks} of ${attr.totalRisks} risk${attr.totalRisks !== 1 ? "s" : ""} remediated` : undefined}
                                            >
                                                {isAddressed && <CheckCircle2 className="h-3 w-3" />}
                                                {control}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    );
};

export default VendorControlMapping;
